import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { clientService } from '@/api/services/client.service';
import { authService } from '@/api/services/auth.service';
import { queryKeys } from '../query-keys';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import type { User } from '@/api/types/auth.types';

export const useProfile = () => {
  const { user } = useAuth();
  const isAdmin = user?.type === 'admin';

  return useQuery({
    queryKey: queryKeys.profile.current(),
    queryFn: async () => {
      // Admin uses /me endpoint, client uses /client/profile
      if (isAdmin) {
        const response = await authService.getCurrentUser();
        // Convert to ApiResponse format
        return {
          success: true,
          data: response.data,
        };
      }
      return clientService.getProfile();
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
    retry: 1, // Retry once on failure
    retryDelay: 1000, // Wait 1 second before retry
    enabled: !!user, // Only fetch if user is logged in - will call appropriate API based on user type
  });
};

export const useUpdateProfile = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { user } = useAuth();
  const { t } = useLanguage();
  const isAdmin = user?.type === 'admin';

  return useMutation({
    mutationFn: (data: Partial<User>) => {
      // Admin profile updates might not be supported, skip for now
      if (isAdmin) {
        return Promise.resolve({ success: true, data: user });
      }
      return clientService.updateProfile(data);
    },
    onSuccess: (response) => {
      if (response.success) {
        queryClient.invalidateQueries({ queryKey: queryKeys.profile.all });
        queryClient.invalidateQueries({ queryKey: queryKeys.auth.currentUser() });
        toast({
          title: t('profile.notifications.updated') || 'تم تحديث الملف الشخصي',
          description: t('profile.notifications.updatedDesc') || 'تم حفظ التغييرات بنجاح',
        });
      }
    },
    onError: (error) => {
      toast({
        title: t('profile.notifications.updateFailed') || 'فشل التحديث',
        description: error instanceof Error ? error.message : (t('profile.notifications.updateFailedDesc') || 'حدث خطأ غير متوقع'),
        variant: 'destructive',
      });
    },
  });
};

export const useUploadDocuments = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useLanguage();

  return useMutation({
    mutationFn: (files: File[]) => {
      return clientService.uploadDocuments(files);
    },
    onSuccess: (response) => {
      if (response.success) {
        queryClient.invalidateQueries({ queryKey: queryKeys.profile.current() });
        toast({
          title: t('profile.notifications.documentsUploaded') || 'تم رفع المستندات',
          description: t('profile.notifications.documentsUploadedDesc') || 'تم رفع المستندات بنجاح',
        });
      }
    },
    onError: (error) => {
      toast({
        title: t('profile.notifications.documentsUploadFailed') || 'فشل رفع المستندات',
        description: error instanceof Error ? error.message : (t('profile.notifications.documentsUploadFailedDesc') || 'حدث خطأ غير متوقع'),
        variant: 'destructive',
      });
    },
  });
};

export const useDeleteDocument = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useLanguage();

  return useMutation({
    mutationFn: (documentId: number) => {
      return clientService.deleteDocument(documentId);
    },
    onSuccess: (response) => {
      if (response.success) {
        queryClient.invalidateQueries({ queryKey: queryKeys.profile.current() });
        toast({
          title: t('profile.notifications.documentDeleted') || 'تم حذف المستند',
          description: t('profile.notifications.documentDeletedDesc') || 'تم حذف المستند بنجاح',
        });
      }
    },
    onError: (error) => {
      toast({
        title: t('profile.notifications.documentDeleteFailed') || 'فشل حذف المستند',
        description: error instanceof Error ? error.message : (t('profile.notifications.documentDeleteFailedDesc') || 'حدث خطأ غير متوقع'),
        variant: 'destructive',
      });
    },
  });
};

export const useUploadProfileImage = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useLanguage();

  return useMutation({
    mutationFn: ({ data, image }: { data: Partial<User>; image?: File }) => {
      return clientService.updateProfileWithImage(data, image);
    },
    onSuccess: (response) => {
      if (response.success) {
        queryClient.invalidateQueries({ queryKey: queryKeys.profile.current() });
        queryClient.invalidateQueries({ queryKey: queryKeys.auth.currentUser() });
        toast({
          title: t('profile.notifications.imageUpdated') || 'تم تحديث الملف الشخصي',
          description: t('profile.notifications.imageUpdatedDesc') || 'تم تحديث الملف الشخصي بنجاح',
        });
      }
    },
    onError: (error) => {
      toast({
        title: t('profile.notifications.imageUpdateFailed') || 'فشل تحديث الملف الشخصي',
        description: error instanceof Error ? error.message : (t('profile.notifications.imageUpdateFailedDesc') || 'حدث خطأ غير متوقع'),
        variant: 'destructive',
      });
    },
  });
};
